import { ExpressionFilterSpecification, GeoJSONFeatureId, GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { assert } from '@deck.gl/core';
import * as turf from '@turf/turf';
import { AirportFunction, EtopsAirport, EtopsData, EtopsPointType } from '@fe/core';
import { FeatureIds, FlightRoutes } from './routes-layer';
import { EtopsColorSettings, FlightPlanSettings } from './types';

type FeatureRole = 'circle-inner' | 'circle-outer' | 'circle-center' | 'point';
type RouteType = 'a' | 'p';
interface FeatureProperty {
    type: RouteType;
    role: FeatureRole;
}

interface PointFeatureProperty extends FeatureProperty {
    tag: string | null;
    aptFunc?: AirportFunction;
    pointType?: EtopsPointType;
}

interface LineFeatureProperty extends FeatureProperty {
    tag?: string | null;
}

export const SOURCE = 'fe-etops-source';
export const FILLS = 'fe-etops-fills';
export const OUTLINES = 'fe-etops-outlines';
export const POINTS = 'fe-etops-points';
export const LABELS = 'fe-etops-labels';
export const LINE_LABELS = 'fe-etops-line-labels';

export class EtopsHelper {
    private _settings: FlightPlanSettings | null = null;
    private _colorSettings: EtopsColorSettings | null = null;
    private map: MaplibreMap | null = null;
    private readonly featureIds: Map<number, FeatureIds>;
    private readonly beforeId?: string;

    constructor(featureIds: Map<number, FeatureIds>, beforeId?: string) {
        this.featureIds = featureIds;
        this.beforeId = beforeId;
    }

    get settings(): FlightPlanSettings | null {
        return this._settings;
    }

    set settings(value: FlightPlanSettings | null) {
        this._settings = value;
        this.showHide();
    }

    get colorSettings(): EtopsColorSettings | null {
        return this._colorSettings;
    }

    set colorSettings(value: EtopsColorSettings | null) {
        this._colorSettings = value;
        if (!this.map) {
            return;
        }

        this.map.setPaintProperty(POINTS, 'icon-color', [
            'case',
            ['==', ['get', 'role'], 'circle-center'],
            this.colorSettings?.aptColor ?? '#000',
            ['==', ['get', 'role'], 'point'],
            this.colorSettings?.pointColor ?? 'rgba(50, 50, 255, 0.69)',
            'transparent'
        ]);

        this.map.setPaintProperty(OUTLINES, 'line-color', [
            'case',
            ['==', ['get', 'role'], 'circle-inner'],
            this.colorSettings?.borderOutlineColor ?? 'rgba(0, 75, 148, 0.39)',
            ['==', ['get', 'role'], 'circle-outer'],
            this.colorSettings?.ruleOutlineColor ?? 'rgba(128, 128, 128, 0.39)',
            'transparent'
        ]);

        this.map.setPaintProperty(FILLS, 'fill-color', [
            'case',
            ['==', ['get', 'role'], 'circle-inner'],
            this.colorSettings?.borderFillColor ?? 'rgba(0, 75, 148, 0.08)',
            ['==', ['get', 'role'], 'circle-outer'],
            this.colorSettings?.ruleFillColor ?? 'rgba(128, 128, 128, 0.05)',
            'transparent'
        ]);
    }

    addToMap(map: MaplibreMap) {
        this.map = map;
        assert(this.map);

        this.map.addSource(SOURCE, {
            type: 'geojson',
            data: turf.featureCollection([])
        });

        this.map.addLayer(
            {
                id: LABELS,
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10,
                    'text-field': ['get', 'tag'],
                    'text-anchor': 'top-left',
                    'text-justify': 'left',
                    'icon-allow-overlap': true,
                    'text-allow-overlap': true,
                    'symbol-placement': 'point',
                    'text-optional': false,
                    'text-offset': [0.0, 0.7]
                },
                paint: {
                    'text-color': ['global-state', 'route-text-color']
                },
                filter: ['any', ['==', ['get', 'role'], 'circle-center'], ['==', ['get', 'role'], 'point']]
            },
            this.beforeId
        );

        this.map.addLayer(
            {
                id: LINE_LABELS,
                type: 'symbol',
                source: SOURCE,
                minzoom: 4,
                layout: {
                    'text-font': ['Open Sans Regular'],
                    'text-size': 10,
                    'text-field': ['get', 'tag'],
                    'symbol-avoid-edges': true,
                    'symbol-placement': 'line',
                    'symbol-spacing': 900,
                    'text-allow-overlap': false,
                    'text-optional': true,
                    'text-anchor': 'center',
                    'text-offset': [0.0, 0.7]
                },
                paint: {
                    'text-color': ['global-state', 'route-text-color']
                },
                filter: ['any', ['==', ['get', 'role'], 'circle-inner'], ['==', ['get', 'role'], 'circle-outer']]
            },
            this.beforeId
        );

        this.map.addLayer(
            {
                id: POINTS,
                type: 'symbol',
                source: SOURCE,
                layout: {
                    'icon-image': ['case', ['==', ['get', 'role'], 'circle-center'], 'fe:circle-filled', 'fe:triangle-filled'],
                    'icon-allow-overlap': true,
                    'icon-size': 0.4,
                    'symbol-placement': 'point'
                },
                paint: {
                    'icon-opacity': 0.69,
                    'icon-color': [
                        'case',
                        ['==', ['get', 'role'], 'circle-center'],
                        this.colorSettings?.aptColor ?? '#000',
                        ['==', ['get', 'role'], 'point'],
                        this.colorSettings?.pointColor ?? 'rgba(50, 50, 255, 0.69)',
                        'transparent'
                    ]
                },
                filter: ['any', ['==', ['get', 'role'], 'circle-center'], ['==', ['get', 'role'], 'point']]
            },
            this.beforeId
        );

        this.map.addLayer(
            {
                id: OUTLINES,
                type: 'line',
                source: SOURCE,
                paint: {
                    'line-width': 2,
                    'line-dasharray': ['step', ['zoom'], ['literal', [1, 1]], 4, ['literal', [2, 2]], 8, ['literal', [3, 3]]],
                    'line-color': [
                        'case',
                        ['==', ['get', 'role'], 'circle-inner'],
                        this.colorSettings?.borderOutlineColor ?? 'rgba(0, 75, 148, .39)',
                        ['==', ['get', 'role'], 'circle-outer'],
                        this.colorSettings?.ruleOutlineColor ?? 'rgba(128, 128, 128, .39)',
                        'transparent'
                    ]
                },
                filter: ['any', ['==', ['get', 'role'], 'circle-inner'], ['==', ['get', 'role'], 'circle-outer']]
            },
            this.beforeId
        );

        this.map.addLayer(
            {
                id: FILLS,
                type: 'fill',
                source: SOURCE,
                layout: {},
                paint: {
                    'fill-color': [
                        'case',
                        ['==', ['get', 'role'], 'circle-inner'],
                        this.colorSettings?.borderFillColor ?? 'rgba(0, 75, 148, 0.08)',
                        ['==', ['get', 'role'], 'circle-outer'],
                        this.colorSettings?.ruleFillColor ?? 'rgba(128, 128, 128, 0.05)',
                        'transparent'
                    ]
                },
                filter: ['any', ['==', ['get', 'role'], 'circle-inner'], ['==', ['get', 'role'], 'circle-outer']]
            },
            this.beforeId
        );

        this.showHide();
    }

    removeFromMap() {
        assert(this.map);

        this.map.removeLayer(OUTLINES);
        this.map.removeLayer(POINTS);
        this.map.removeLayer(FILLS);
        this.map.removeLayer(LABELS);
        this.map.removeLayer(LINE_LABELS);
        this.map.removeSource(SOURCE);
        this.map = null;
    }

    remove(id: number) {
        assert(this.map);

        const source = this.map.getSource(SOURCE) as GeoJSONSource;
        source.updateData({
            remove: this.featureIds.get(id)?.etops as GeoJSONFeatureId[] | undefined
        });
    }

    removeAll() {
        const source = this.map?.getSource(SOURCE) as GeoJSONSource;
        source?.updateData({
            removeAll: true
        });
    }

    upsert(id: number, routes: FlightRoutes): GeoJSON.Feature[] {
        const source = this.map!.getSource(SOURCE) as GeoJSONSource;
        assert(source);
        const { atc, planned } = routes || {};

        const atcFeatures = atc?.etops ? [...this.toPolygons(id, atc!.etops!, 'a'), ...this.toPoints(id, atc!.etops!, 'a')] : [];
        const plannedFeatures = planned?.etops
            ? [...this.toPolygons(id, planned!.etops!, 'p'), ...this.toPoints(id, planned!.etops!, 'p')]
            : [];
        const features = [...atcFeatures, ...plannedFeatures];

        const toRemove = this.featureIds.get(id)?.etops ?? [];
        source.updateData({
            add: features,
            remove: toRemove
        });

        return features;
    }

    private showHide() {
        if (!this.map || !this.settings) {
            return;
        }

        const { show, showLabels, showEtops, showAtc, showPlanned } = this.settings;
        if (!this.map || !this.settings) {
            return;
        }

        this.map.setLayoutProperty(FILLS, 'visibility', show && showEtops ? 'visible' : 'none');
        this.map.setLayoutProperty(OUTLINES, 'visibility', show && showEtops ? 'visible' : 'none');
        this.map.setLayoutProperty(POINTS, 'visibility', show && showEtops ? 'visible' : 'none');
        this.map.setLayoutProperty(LABELS, 'visibility', show && showLabels && showEtops ? 'visible' : 'none');
        this.map.setLayoutProperty(LINE_LABELS, 'visibility', show && showLabels && showEtops ? 'visible' : 'none');

        const typeFilter: ExpressionFilterSpecification = ['any'];
        if (showPlanned) {
            typeFilter.push(['==', ['get', 'type'], 'p']);
        }

        if (showAtc) {
            typeFilter.push(['==', ['get', 'type'], 'a']);
        }

        this.map.setFilter(FILLS, [
            'all',
            ['any', ['==', ['get', 'role'], 'circle-inner'], ['==', ['get', 'role'], 'circle-outer']],
            typeFilter
        ]);
        this.map.setFilter(OUTLINES, [
            'all',
            ['any', ['==', ['get', 'role'], 'circle-inner'], ['==', ['get', 'role'], 'circle-outer']],
            typeFilter
        ]);
        this.map.setFilter(POINTS, [
            'all',
            ['all', ['!=', ['get', 'role'], 'circle-inner'], ['!=', ['get', 'role'], 'circle-outer']],
            typeFilter
        ]);
        this.map.setFilter(LABELS, [
            'all',
            ['any', ['==', ['get', 'role'], 'circle-center'], ['==', ['get', 'role'], 'point']],
            typeFilter
        ]);

        this.map.setFilter(LINE_LABELS, [
            'all',
            ['any', ['==', ['get', 'role'], 'circle-inner'], ['==', ['get', 'role'], 'circle-outer']],
            typeFilter
        ]);
    }

    private toPolygons(
        id: number,
        data: EtopsData,
        type: RouteType
    ): GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.LineString, LineFeatureProperty>[] {
        const { airports, borderDistance = 0, ruleDistance = 0, borderTime, ruleTime } = data;
        if (!airports || (borderDistance === 0 && ruleDistance === 0)) {
            return [];
        }
        let ndx = 0;
        const features: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.LineString, LineFeatureProperty>[] = [];
        for (const apt of airports) {
            if (borderDistance !== 0) {
                const feature = turf.circle(apt.position, borderDistance as number, {
                    steps: 256,
                    units: 'nauticalmiles',
                    properties: {
                        type,
                        aptFunc: apt.aptFunc,
                        role: 'circle-inner' as FeatureRole,
                        tag: getRingTag(borderTime, borderDistance, apt)
                    }
                });
                feature.id = `${id}-etops-${ndx++}-cp-${type}`;
                features.push(feature);
            }

            if (ruleDistance !== 0 && apt.aptFunc === AirportFunction.suitable) {
                const feature = turf.circle(apt.position, ruleDistance as number, {
                    steps: 256,
                    units: 'nauticalmiles',
                    properties: {
                        type,
                        aptFunc: apt.aptFunc,
                        role: 'circle-outer' as FeatureRole,
                        tag: getRingTag(ruleTime, ruleDistance, apt)
                    }
                });
                feature.id = `${id}-etops-${ndx++}-cp-${type}`;
                features.push(feature);
            }
        }

        return features;
    }

    private toPoints(id: number, data: EtopsData, type: RouteType): GeoJSON.Feature<GeoJSON.Point, PointFeatureProperty>[] {
        const { points, airports } = data || {};
        const etopsPoints =
            points?.map((x, i) =>
                turf.point(
                    x.position,
                    {
                        type,
                        tag: pointTag(x.type),
                        pointType: x.type,
                        role: 'point' as FeatureRole
                    },
                    {
                        id: `${id}-etops-${i}-p-${type}`
                    }
                )
            ) ?? [];

        const aptPoints =
            airports?.map((x, i) =>
                turf.point(
                    x.position,
                    {
                        type,
                        tag: x.icaoId ?? x.iataId,
                        aptFunc: x.aptFunc,
                        role: 'circle-center' as FeatureRole
                    },
                    {
                        id: `${id}-etops-${i}-cc-${type}`
                    }
                )
            ) ?? [];

        return [...etopsPoints, ...aptPoints];
    }
}

function pointTag(pointType: EtopsPointType): string {
    switch (pointType) {
        case EtopsPointType.etopsEntry:
            return 'EEP';
        case EtopsPointType.etopsExit:
            return 'EXP';
        case EtopsPointType.etp:
            return 'ETP';
        case EtopsPointType.coverageEntry:
            return 'EN';
        case EtopsPointType.coverageExit:
            return 'EX';
        case EtopsPointType.terrainAvoidance:
            return 'TA';
        case EtopsPointType.equalFuel:
            return 'EFP';
        default:
            return '';
    }
}

function getRingTag(time: number | null, distance: number | null, apt: EtopsAirport): string {
    return `${time != null ? Math.round(time) + ' min - ' : ''}${distance != null ? formatDistance(distance) : ''} - ${apt.icaoId ?? apt.iataId}`;
}

function formatDistance(distance: number): string {
    const fe = (window as any).fe;
    return fe?.UnitFormatter ? fe.UnitFormatter.formatDistance(distance) : Math.round(distance).toLocaleString('en-US') + ' NM';
}
